import { useRef, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';

import { fetch_month_runs, post_month_run } from '../../api';
import {
  RUN_CHECKING_TEXT,
  RUN_CHECK_FAILED_TEXT,
  RUN_NOT_STARTED_TEXT,
  own_run_uuid,
  run_request_failure
} from './adapters';
import { run_create_payload } from './params';

/* starts a close or reopen run for the close/reopen dialogs. */
const useRunCreate = ({
  pay_month,
  on_started,
  on_precheck,
  on_refused,
  new_request_id = uuidv4
}) => {
  // '' | 'sending' | 'checking' | 'uncertain'
  const [phase, setPhase] = useState('');
  const [notice, setNotice] = useState('');
  const id_ref = useRef('');
  const body_ref = useRef(null);
  // Bumped by reset: an answer never lands in a later opening of the dialog.
  const seq = useRef(0);
  const handlers = useRef(null);
  handlers.current = { on_started, on_precheck, on_refused };

  const request_id = () => {
    if (!id_ref.current) id_ref.current = new_request_id();
    return id_ref.current;
  };

  const found = (run_uuid, run) => {
    id_ref.current = '';
    setPhase('');
    setNotice('');
    handlers.current.on_started(run_uuid, run);
  };

  const check = (body, s) => {
    setPhase('checking');
    setNotice(RUN_CHECKING_TEXT);
    fetch_month_runs(body.pay_month)
      .then((data) => {
        if (s !== seq.current) return;
        const run_uuid = own_run_uuid(data, body.client_request_id);
        if (run_uuid) {
          found(run_uuid, null);
          return;
        }
        setPhase('uncertain');
        setNotice(RUN_NOT_STARTED_TEXT);
      })
      .catch(() => {
        if (s !== seq.current) return;
        setPhase('uncertain');
        setNotice(RUN_CHECK_FAILED_TEXT);
      });
  };

  const post = (body) => {
    seq.current += 1;
    const s = seq.current;
    body_ref.current = body;
    setPhase('sending');
    setNotice('');
    post_month_run(body)
      .then((data) => {
        if (s !== seq.current) return;
        if (data.requires_override === true) {
          id_ref.current = '';
          setPhase('');
          handlers.current.on_precheck(data.precheck);
          return;
        }
        // A 200 with neither a run nor a pre-check is not an answer we can trust: look for the run.
        if (!data.run || !data.run.run_uuid) {
          check(body, s);
          return;
        }
        found(data.run.run_uuid, data.run);
      })
      .catch((e) => {
        if (s !== seq.current) return;
        const failure = run_request_failure(e);
        if (failure.kind === 'dropped') {
          check(body, s);
          return;
        }
        id_ref.current = '';
        setPhase('');
        handlers.current.on_refused(failure, e);
      });
  };

  // fields: run_create_payload's input less pay_month and client_request_id.
  const send = (fields) =>
    post(run_create_payload({ ...fields, pay_month, client_request_id: request_id() }));

  const retry = () => {
    if (body_ref.current) post(body_ref.current);
  };

  const reset = () => {
    seq.current += 1;
    id_ref.current = '';
    body_ref.current = null;
    setPhase('');
    setNotice('');
  };

  return {
    send,
    retry,
    reset,
    notice,
    busy: phase === 'sending' || phase === 'checking',
    uncertain: phase === 'uncertain'
  };
};

export default useRunCreate;
